import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/client';

function photoUrl(photoPath) {
  if (!photoPath) return null;
  return `/uploads/staff/${String(photoPath).split('/').pop()}`;
}

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const res = await api.get('/staff', { params: { search, staffType: filterType, status: filterStatus, limit: 5000 } });
    setStaff(res.data.staff);
  }, [search, filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  async function handleStatusToggle(member) {
    const nextStatus = member.status === 'active' ? 'inactive' : 'active';
    if (!window.confirm(`Mark ${member.staff_name} as ${nextStatus}?`)) return;
    await api.patch(`/staff/${member.id}/status`, { status: nextStatus });
    load();
  }

  async function downloadTemplate() {
    const res = await api.get('/staff/import-template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url; link.download = 'staff-import-template.xlsx'; link.click();
  }

  async function handleExport() {
    const res = await api.get('/staff/export', { params: { format: 'excel' }, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url; link.download = 'staff-export.xlsx'; link.click();
  }

  async function handleImport() {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const data = new FormData();
      data.append('file', importFile);
      const res = await api.post('/staff/import', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      load();
    } catch (err) {
      setImportResult({ created: 0, failed: 0, errors: [{ row: '-', message: err.response?.data?.error || 'Import failed' }] });
    } finally {
      setImporting(false);
    }
  }

  function closeImportModal() {
    setShowImport(false);
    setImportFile(null);
    setImportResult(null);
  }

  return (
    <Layout role="schoolAdmin">
      <div className="page-header">
        <div><h1 className="page-title">Staff Management</h1></div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={() => setShowImport(true)}><i className="fas fa-file-excel"></i> Import Excel</button>
          <button className="btn btn-outline" onClick={handleExport}><i className="fas fa-file-download"></i> Export Excel</button>
          <button className="btn btn-primary" onClick={() => navigate('/staff/new')}><i className="fas fa-plus"></i> Add Staff</button>
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 20 }}>
        <div className="filter-row">
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input type="text" placeholder="Search by name or mobile" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
          <select className="form-control" style={{ width: 160 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Staff Types</option>
            <option value="teaching">Teaching</option>
            <option value="non_teaching">Non-Teaching</option>
          </select>
          <select className="form-control" style={{ width: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button className="btn btn-outline" onClick={() => { setSearch(''); setFilterType(''); setFilterStatus(''); }}><i className="fas fa-times"></i></button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Staff List</h3>
          <span className="badge badge-info">{staff.length} staff</span>
        </div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Photo</th><th>Name</th><th>Mobile</th><th>Designation</th><th>Type</th>
                <th>Class/Division</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.length === 0 ? (
                <tr><td colSpan={8}>No staff members found.</td></tr>
              ) : staff.map(s => (
                <tr key={s.id}>
                  <td>
                    {photoUrl(s.photo_path)
                      ? <img src={photoUrl(s.photo_path)} alt={s.staff_name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                      : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>{(s.staff_name || '?').charAt(0)}</div>}
                  </td>
                  <td><span style={{ color: 'var(--primary)', cursor: 'pointer' }} onClick={() => navigate(`/staff/${s.id}`)}>{s.staff_name}</span></td>
                  <td>{s.mobile_number}</td>
                  <td>{s.designation === 'Other' && s.custom_designation ? s.custom_designation : s.designation}</td>
                  <td>{s.staff_type === 'teaching' ? 'Teaching' : 'Non-Teaching'}</td>
                  <td>{s.assigned_standard ? `${s.assigned_standard}-${s.assigned_division || ''}` : '-'}</td>
                  <td><span className={`badge ${s.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>{s.status === 'active' ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <button className="btn-icon" title="Edit" onClick={() => navigate(`/staff/${s.id}/edit`)}><i className="fas fa-edit"></i></button>
                    <button className="btn-icon" title={s.status === 'active' ? 'Deactivate' : 'Activate'} onClick={() => handleStatusToggle(s)}>
                      <i className={`fas ${s.status === 'active' ? 'fa-user-slash' : 'fa-user-check'}`} style={{ color: s.status === 'active' ? 'var(--danger)' : 'var(--success)' }}></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showImport && (
        <div className="modal-overlay show" style={{ display: 'flex' }} onClick={closeImportModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fas fa-file-excel" style={{ color: 'var(--success)', marginRight: 8 }}></i>Import Staff from Excel</h3>
              <button className="modal-close" onClick={closeImportModal}>×</button>
            </div>
            <div className="modal-body">
              {!importResult ? (
                <>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
                    Download the template, fill it in (one staff member per row), then upload it back here.
                  </p>
                  <button className="btn btn-outline btn-sm" style={{ marginBottom: 20 }} onClick={downloadTemplate}><i className="fas fa-download"></i> Download Template</button>

                  <div className="form-group">
                    <label className="form-label">Upload filled file (.xlsx, .xls, or .csv)</label>
                    <input type="file" accept=".xlsx,.xls,.csv" className="form-control" onChange={e => setImportFile(e.target.files[0] || null)} />
                  </div>
                </>
              ) : (
                <div>
                  <div style={{ background: importResult.failed > 0 ? '#FFFBEB' : '#ECFDF5', color: importResult.failed > 0 ? '#92400E' : 'var(--success)', padding: 14, borderRadius: 8, marginBottom: 14 }}>
                    <strong>{importResult.created}</strong> staff members imported successfully.
                    {importResult.failed > 0 && <span> <strong>{importResult.failed}</strong> rows failed.</span>}
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: 12 }}>
                      {importResult.errors.map((e, i) => (
                        <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>Row {e.row}: {e.message || e.error}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeImportModal}>{importResult ? 'Close' : 'Cancel'}</button>
              {!importResult && (
                <button className="btn btn-primary" onClick={handleImport} disabled={!importFile || importing}>
                  <i className="fas fa-upload"></i> {importing ? 'Importing...' : 'Upload & Import'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
